import {
    Box,
    Spinner,
    Center,
    Flex,
    Heading,
    Text,
    Button,
    VStack,
} from "@chakra-ui/react";
import Iframe from "react-iframe";
import PageHeader from "components/layout/PageHeader";
import { AlertsContext } from "providers/alerts";
import { ReactNode, useContext, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRequest } from "utils/api";
import moment from "moment";
import UserAvatar from "components/elements/UserAvatar";
import { useQuery } from "@tanstack/react-query";
import { getErrorMessage } from "utils/errorHandler";

const DocumentDetail = ({
    title,
    content,
}: {
    title: string;
    content: ReactNode;
}) => {
    return (
        <Box mb="12px">
            <Heading
                color="gray.200"
                fontWeight="500"
                fontSize="12px"
                mt="8px"
                mb="4px"
            >
                {title}
            </Heading>
            {content}
        </Box>
    );
};

function Document() {
    const { id } = useParams();

    const { showError } = useContext(AlertsContext);

    const [downloadUrl, setDownloadUrl] = useState("");
    const [downloading, setDownloading] = useState(false);

    const downloadDocument = async () => {
        try {
            setDownloading(true);
            const response = await apiRequest("/supabase/documents/download", {
                id,
            });

            setDownloadUrl(response.signedUrl);
        } catch (err) {
            showError({
                title: "Download Failed",
                description: getErrorMessage(err),
            });
        } finally {
            setDownloading(false);
        }
    };

    const { data: document } = useQuery({
        queryKey: ["documents-view", id],
        queryFn: async () =>
            await apiRequest("/supabase/documents/select", { id }),
        select: (data) => data?.documents?.[0],
    });

    return document ? (
        <>
            <PageHeader
                title="Document"
                breadcrumbs={[
                    {
                        title: document.loan?.name,
                        to: `/loans/${document.loan?.id}`,
                    },
                    {
                        title: "Documents",
                        to: `/loans/${document.loan?.id}?tab=documents`,
                    },
                    {
                        title: document.name,
                        to: `/documents/${document?.id}/documents`,
                    },
                ]}
            />
            <Box bg="white" borderRadius="20px" px="26px">
                <Flex>
                    <Box
                        display="flex"
                        flexDir="column"
                        h="calc(100vh - 200px)"
                        w="260px"
                        pr="26px"
                        py="26px"
                        borderRightColor="gray.100"
                        borderRightWidth="1px"
                    >
                        <Heading fontWeight="600" fontSize="18px" mb="6px">
                            Document Details
                        </Heading>
                        {document.loan && (
                            <Link to={`/loans/${document.loan?.id}`}>
                                <Text
                                    color="primary"
                                    fontWeight="500"
                                    fontSize="14px"
                                >
                                    {document.loan.name}
                                </Text>
                            </Link>
                        )}

                        <Box mt="8px">
                            <DocumentDetail
                                title="Name"
                                content={
                                    <Text color="secondary" fontSize="14px">
                                        {document?.name}
                                    </Text>
                                }
                            />
                            <DocumentDetail
                                title="Size"
                                content={
                                    <Text color="secondary" fontSize="14px">
                                        {(
                                            document.file_size /
                                            1024 /
                                            1024
                                        ).toFixed(1)}
                                        MB
                                    </Text>
                                }
                            />
                            <DocumentDetail
                                title="Date"
                                content={
                                    <Text color="secondary" fontSize="14px">
                                        {moment(document.created_at).format(
                                            "MM/DD/YY"
                                        )}
                                    </Text>
                                }
                            />

                            <DocumentDetail
                                title="Uploader"
                                content={
                                    <Box mt="6px">
                                        <Flex alignItems="center">
                                            <UserAvatar
                                                size="40px"
                                                mr="8px"
                                                user={document.user}
                                                hideOnline
                                            />
                                            <Flex flexDir="column">
                                                <Text
                                                    color="secondary"
                                                    fontSize="14px"
                                                    fontWeight="400"
                                                    mb="6px"
                                                >
                                                    {document.user.first_name}{" "}
                                                    {document.user.last_name}
                                                </Text>
                                                <Text
                                                    color="gray.200"
                                                    fontSize="12px"
                                                    lineHeight="12px"
                                                    mt="0px"
                                                >
                                                    {document.user.type ===
                                                    "borrower"
                                                        ? document.company.name
                                                        : document.user
                                                              .job_title}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                    <Box flexGrow="1">
                        {downloadUrl ? (
                            <Iframe
                                url={downloadUrl}
                                width="100%"
                                height="100%"
                                id=""
                                className=""
                                display="block"
                                position="relative"
                            />
                        ) : (
                            <Center h="100%">
                                <VStack w="400px">
                                    <Heading
                                        textAlign="center"
                                        fontSize="14px"
                                        lineHeight="24px"
                                        fontWeight="400"
                                        color="gray.200"
                                    >
                                        Documents are protected by default. You
                                        can view the document by clicking below.
                                    </Heading>
                                    <Button
                                        isLoading={downloading}
                                        onClick={downloadDocument}
                                        mt="12px"
                                    >
                                        View Document
                                    </Button>
                                </VStack>
                            </Center>
                        )}
                    </Box>
                </Flex>
            </Box>
        </>
    ) : (
        <Spinner />
    );
}

export default Document;
